import { useState, useEffect } from "react";
import axios from "axios";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import Cards from "../../components/Cards";
import { FaFileInvoiceDollar, FaTruck, FaUndo, FaUsers, FaBoxOpen } from "react-icons/fa";
import { FcFullTrash } from "react-icons/fc";
import { TbSortAscending } from "react-icons/tb";
import { useAuth } from "../../utils/AuthContext";
import { Link } from "react-router-dom";
import { TiArrowRight } from "react-icons/ti";
import PharmacyPerformanceChart from "../../components/PharmacyPerformanceChart";
import TopDoctorCard from "../../components/TopDoctorCard";
import StockBreakdownChart from "../../components/StockBreakdownChart";
import StockNoticeModal from "../../components/Pharmacy/StockNoticeModal";
import TechnicalSupportNotice from "../../components/TechnicalSupportNotice";

const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const BASE_URL = "https://api.muktihospital.com/api";

const PharmacistDashboard = () => {
  const [dashboardData, setDashboardData] = useState([]);
  const [doctorsData, setDoctorsData] = useState([]); 
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [allStockItems, setAllStockItems] = useState([]);
  const [pharmacyBills, setPharmacyBills] = useState([]);
  const [billsLoading, setBillsLoading] = useState(true);
  const [isStockModalOpen, setIsStockModalOpen] = useState(false);

 useEffect(() => {
    const loadPharmacistData = async () => {
      setLoading(true);
      try {
        const api = axios.create({ baseURL: BASE_URL, headers: { "x-api-key": API_KEY } });

        const today = new Date().toISOString().split("T")[0];
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth() + 1;
        const monthPadded = String(month).padStart(2, '0');

        const [
          stockRes, 
          todayBillsRes, 
          monthBillsRes, 
          yearBillsRes,
          monthPurchasesRes, 
          yearPurchasesRes,
          supplierReturnRes,
          customerReturnRes,
          wastageRes,
          doctorsRes,
          pharmacyBillsRes
        ] = await Promise.all([
          api.get("/medicine-purchase").catch(() => ({ data: { purchases: [] } })),
          api.get(`/medicine-bill/bills/range?startDate=${today}&endDate=${today}`),
          api.get(`/medicine-bill/bills/monthly?year=${year}&month=${monthPadded}`),
          api.get(`/medicine-bill/bills/yearly?year=${year}`),
          api.get(`/medicine-purchase/purchases/monthly?year=${year}&month=${monthPadded}`),
          api.get(`/medicine-purchase/purchases/yearly?year=${year}`),
          api.get("/return/supplier-returns").catch(() => ({ data: { returns: [], total: 0 } })),
          api.get("/return/customer-returns").catch(() => ({ data: { returns: [], total: 0 } })),
          api.get("/return/wastage-returns").catch(() => ({ data: { returns: [], total: 0 } })),
          api.get(`/doctor`).catch(() => ({ data: [] })),
          api.get('/medicine-bill/list/?page=1&limit=5').catch(() => ({ data: { data: { bills: [] } } }))
        ]);

        setDoctorsData(doctorsRes.data || []);
        setPharmacyBills(pharmacyBillsRes.data?.data?.bills || pharmacyBillsRes.data?.bills || []);
        setBillsLoading(false);
        const purchases = stockRes.data?.purchases || [];
        const stockItems = purchases.flatMap(p => p.items || []);
        setAllStockItems(stockItems);
        
        // Pharmacy Bills data
        const todayBillsCount = todayBillsRes.data?.totalTransactions || 0;
        const todayAmount = parseFloat(todayBillsRes.data?.totalAmount) || 0;
        const thisMonthBillsCount = monthBillsRes.data?.bills?.length || 0;
        const thisMonthAmount = parseFloat(monthBillsRes.data?.totalAmount) || 0;
        const thisYearBillsCount = yearBillsRes.data?.bills?.length || 0;
        const thisYearAmount = parseFloat(yearBillsRes.data?.totalAmount) || 0;
        
        // Purchase Bills data
        const todayPurchases = purchases.filter(p => p.purchaseDate && p.purchaseDate.startsWith(today));
        const todayPurchasesCount = todayPurchases.length;
        const todayPurchasesAmount = todayPurchases.reduce((sum, p) => sum + (p.netAmount || 0), 0);
        const thisMonthPurchasesCount = monthPurchasesRes.data?.purchases?.length || 0;
        const thisMonthPurchasesAmount = parseFloat(monthPurchasesRes.data?.totalAmount) || 0;
        const thisYearPurchasesCount = yearPurchasesRes.data?.purchases?.length || 0;
        const thisYearPurchasesAmount = parseFloat(yearPurchasesRes.data?.totalAmount) || 0;

        // Stock Data
        const allStockItems = purchases.flatMap(p => p.items || []);
        const totalStock = allStockItems.reduce((sum, item) => sum + (item.availableQuantity || 0), 0);
        const expiredInStockCount = allStockItems.filter(item => new Date(item.expiryDate) < new Date() && item.availableQuantity > 0).length;
        const outOfStockCount = allStockItems.filter(item => new Date(item.expiryDate) > new Date() && (item.availableQuantity || 0) <= 0).length;

        // Process Returns and Wastage Data
        const supplierReturns = supplierReturnRes.data?.returns || [];
        const customerReturns = customerReturnRes.data?.returns || [];
        const wastageItems = wastageRes.data?.returns || [];

        const supplierReturnTotalAmount = supplierReturns.reduce((sum, item) => sum + parseFloat(item.netReturn || 0), 0);
        const supplierReturnMonthCount = supplierReturns.filter(item => new Date(item.returnedAt).getMonth() === now.getMonth() && new Date(item.returnedAt).getFullYear() === year).length;
        
        const customerReturnTotalAmount = customerReturns.reduce((sum, item) => sum + parseFloat(item.netReturn || 0), 0);
        const customerReturnMonthCount = customerReturns.filter(item => new Date(item.returnedAt).getMonth() === now.getMonth() && new Date(item.returnedAt).getFullYear() === year).length;
        
        const wastageTotalAmount = wastageItems.reduce((sum, item) => sum + parseFloat(item.totalAmount || 0), 0);
        const wastageMonthCount = wastageItems.filter(item => new Date(item.wastedAt).getMonth() === now.getMonth() && new Date(item.wastedAt).getFullYear() === year).length;
        
        const cards = [
          {
            category: "Stock Overview",
            icon: <FaBoxOpen className="text-blue-500 text-xl" />,
            total: totalStock.toLocaleString(),
            status: "Total Items",
            cardListDetails: [
              { label: "Expired in Stock", value: expiredInStockCount },
              { label: "Out of Stock", value: outOfStockCount },
            ],
            onClick: () => setIsStockModalOpen(true),
          },
          {
            category: "Pharmacy Bills",
            icon: <FaFileInvoiceDollar className="text-green-500 text-xl" />,
            total: todayBillsCount,
            status: "Today",
            cardListDetails: [
              { label: "Today's Amount", value: `৳${todayAmount.toLocaleString()}` },
              { label: "This Month", value: `${thisMonthBillsCount} bills (৳${thisMonthAmount.toLocaleString()})` },
              { label: "This Year", value: `${thisYearBillsCount} bills (৳${thisYearAmount.toLocaleString()})` },
            ],
            link: "/pharmacy-bill"
          },
          {
            category: "Supplier Purchases",
            icon: <FaTruck className="text-teal-500 text-xl" />,
            total: todayPurchasesCount,
            status: "Today",
            cardListDetails: [
              { label: "Today's Amount", value: `৳${todayPurchasesAmount.toLocaleString()}` },
              { label: "This Month", value: `${thisMonthPurchasesCount} bills (৳${thisMonthPurchasesAmount.toLocaleString()})` },
              { label: "This Year", value: `${thisYearPurchasesCount} bills (৳${thisYearPurchasesAmount.toLocaleString()})` },
            ],
            link: "/purchase-medicine"
          },
          {
            category: "Supplier Returns",
            icon: <FaUndo className="text-orange-500 text-xl" />,
            total: supplierReturnRes.data?.total || 0,
            status: "Total",
            cardListDetails: [
                { label: "This Month", value: supplierReturnMonthCount },
                { label: "Total Value", value: `৳${supplierReturnTotalAmount.toLocaleString()}` },
            ],
            link: "/supplier-return-list"
          },
          {
            category: "Customer Returns",
            icon: <FaUsers className="text-red-500 text-xl" />,
            total: customerReturnRes.data?.total || 0,
            status: "Total",
            cardListDetails: [
                { label: "This Month", value: customerReturnMonthCount },
                { label: "Total Value", value: `৳${customerReturnTotalAmount.toLocaleString()}` },
            ],
            link: "/invoice-return-list"
          },
          {
            category: "Wastage",
            icon: <FcFullTrash className="text-gray-500 text-xl" />,
            total: wastageRes.data?.total || 0,
            status: "Total Items",
            cardListDetails: [
                { label: "This Month", value: wastageMonthCount },
                { label: "Total Value", value: `৳${wastageTotalAmount.toLocaleString()}` },
            ],
            link: "/wastage-return-list"
          },
        ];
        
        setDashboardData(cards);

      } catch (error) {
        console.error("Error fetching pharmacist dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadPharmacistData();
  }, []);

  return (
    <div className="md:py-4">
       {/* Stock Notice Modal */}
      <StockNoticeModal />
       {/* Top Title Area */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <h1 className="font-semibold text-lg">
          Welcome back, {user?.name || "Pharmacist"} 👋
        </h1>
        <div>
          <Link to="/pharmacy-bill">
            <button className="py-2 px-4 bg-[#73CB64] hover:bg-green-700 text-white rounded-md flex items-center gap-2 text-sm">
              <TbSortAscending /> Add Bill
            </button>
          </Link>
        </div>
      </div>
      <div className="py-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-3 gap-6">
          {loading ? (
            Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={index} height={150} />
            ))
          ) : (
            dashboardData.map((card, i) => (
              <Cards key={i} data={card} isEven={i % 2 === 0} showLinkIcon={true} />
            ))
          )}
        </div>
      </div>
      {/* Main Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-y-6 xl:gap-y-0 xl:gap-6">
        <TechnicalSupportNotice />
        {/* Patient Stats */}
        <div className="col-span-2 bg-white shadow rounded-lg">
          <div className="flex flex-wrap gap-3 justify-between p-4 border-b">
            <h2 className="font-medium">
              Monthly Performance (Sales vs Purchases)
            </h2>
            <ul className="flex gap-2">
              {["All", "1M", "6M", "1Y"].map((t) => (
                <li
                  key={t}
                  className="w-9 py-1 text-center text-sm bg-gray-100 hover:bg-gray-200 rounded-sm cursor-pointer"
                >
                  {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="p-4">
            {loading ? <Skeleton height={400} /> : <PharmacyPerformanceChart />}
          </div>
        </div>
      </div>
      {/* Top Doctors & Breakdown */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-y-6 xl:gap-y-0 xl:gap-6 mt-6">
        {/* Top Doctors */}
        <div className="col-span-2 bg-white shadow rounded-lg">
            <div className="p-4 border-b">
              <h2 className="font-medium">Top Doctors</h2>
            </div>
            <div className="p-5 grid sm:grid-cols-2 lg:grid-cols-3  gap-4">
              {loading ? (
              Array.from({ length: 6 }).map((_, index) => <Skeleton key={index} height={120} />)
            ) : doctorsData.length > 0 ? (
              [...doctorsData]
                .sort((a, b) => {
                  const patientsB = parseInt(b.translations.en.patientAttended) || 0;
                  const patientsA = parseInt(a.translations.en.patientAttended) || 0;
                  if (patientsB !== patientsA) {
                    return patientsB - patientsA;
                  }
                  const experienceB = parseInt(b.translations.en.yearsOfExperience) || 0;
                  const experienceA = parseInt(a.translations.en.yearsOfExperience) || 0;
                  return experienceB - experienceA;
                })
                .slice(0, 6)
                .map((doc) => (
                  <TopDoctorCard key={doc.id} doctor={doc} isAdmin={false} />
                ))
            ) : (
              <p className="col-span-3 text-center text-gray-500 py-4">No doctors available.</p>
            )}
            </div>
            <div className="p-4 border-t">
              <Link to="https://muktihospital.com/doctor">
                <button className="px-3 py-2 bg-[#73CB64] hover:bg-M-primary-color/90 transition-all duration-300 text-white rounded-md flex items-center gap-1 text-sm">
                  See All Doctors <TiArrowRight />
                </button>
              </Link>
            </div>
          </div>

        {/* Patient Breakdown */}
        <div className="bg-white shadow rounded-lg">
          <div className="flex flex-wrap gap-3 justify-between p-4 border-b">
            <h2 className="font-medium">Stock Breakdown by Category</h2>
          </div>
          <div className="p-5">
            {loading ? (
              <Skeleton height={400} />
            ) : (
              <StockBreakdownChart stockItems={allStockItems} />
            )}
          </div>
        </div>
      </div>
       {/* PHARMACY BILLS TABLE SECTION --- */}
    <div className="mt-6 bg-white shadow rounded-lg">
      <div className="flex justify-between items-center p-4 border-b">
        <h2 className="font-medium">Recent Pharmacy Bills</h2>
        <Link to="/pharmacy-bill">
          <button className="px-3 py-2 bg-[#73CB64] hover:bg-M-primary-color/90 transition-all duration-300 text-white rounded-md flex items-center gap-1 text-sm">
            View All <TiArrowRight />
          </button>
        </Link>
      </div>
      <div className="p-4 overflow-x-auto">
        {billsLoading ? (
          <Skeleton height={200} />
        ) : (
          <table className="min-w-full w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-50">
                <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase">Bill No</th>
                <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase">Net Amount (৳)</th>
                <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase">Due Amount (৳)</th>
                <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted By</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pharmacyBills.length > 0 ? (
                pharmacyBills.map((bill) => (
                  <tr key={bill.id} className="hover:bg-gray-50">
                    <td className="p-3 text-sm text-gray-700">{bill.billNumber || '-'}</td>
                    <td className="p-3 text-sm text-gray-700">
                      {bill.createdAt ? new Date(bill.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '-'}
                    </td>
                    <td className="p-3 text-sm text-gray-700">{bill.netAmount?.toFixed(2) || '0.00'}</td>
                    <td className="p-3 text-sm text-gray-700">{bill.dueAmount?.toFixed(2) || '0.00'}</td>
                    <td className="p-3 text-sm text-gray-700">{bill.userName || 'Unknown'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center py-5 text-gray-500">
                    No recent bills found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
     <StockNoticeModal
        isOpen={isStockModalOpen} 
        onClose={() => setIsStockModalOpen(false)} 
      />
    </div>
  );
};

export default PharmacistDashboard;